import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { Button, DateInput, Input } from "src/components/common/FormComponents";
import { useAppSelector } from "src/redux/store";
import ViewForm from "src/components/common/ViewForm";
import errorMessage from "src/constants/errorMessage";
import { updateSeedlingById } from "src/utils/api/dashboard";

type UpdateSeedlingModalProps = {
  seedling: any;
  onClose: () => void;
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const toDateOrNull = (value?: string) =>
  value && DATE_PATTERN.test(value) ? value : null;

export const UpdateSeedlingModal = ({
  seedling,
  onClose,
}: UpdateSeedlingModalProps) => {
  const user = useAppSelector((state) => state.root.user.data);
  const isDno = user?.role === "Implementing Agency" && user?.userType === null;
  const [saveError, setSaveError] = useState("");
  const proposedSeedlings = seedling?.numberOfSeedlings ?? 0;

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    trigger,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      numberOfPlantedSeedlings: seedling?.numberOfPlantedSeedlings ?? 0,
      fromDate: seedling?.fromDate ?? "",
      toDate: seedling?.toDate ?? "",
    },
  });

  const fromDate = watch("fromDate");

  useEffect(() => {
    if (getValues("toDate")) {
      trigger("toDate");
    }
  }, [fromDate, getValues, trigger]);

  const { mutate, isLoading } = useMutation(
    "updateSeedlingDetails",
    (data: any) => updateSeedlingById(seedling.id, data),
    {
      onSuccess: () => {
        onClose();
      },
      onError: (error: any) =>
        setSaveError(
          error?.response?.data?.message || "Failed to update seedling details"
        ),
    }
  );

  const onSubmit = (data: any) => {
    if (!isDno) return;
    mutate({
      landOwnershipEpid: seedling.landOwnershipEpid,
      commonName: seedling.commonName,
      botanicalName: seedling.botanicalName,
      numberOfSeedlings: seedling.numberOfSeedlings,
      numberOfPlantedSeedlings: +data.numberOfPlantedSeedlings,
      fromDate: toDateOrNull(data.fromDate),
      toDate: toDateOrNull(data.toDate),
    });
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "18px" }}>
        Update Seedling Details
      </DialogTitle>
      <DialogContent>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        {seedling.pendingQueryCount > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            After updating, send your reply from the query history. Another
            update requires a new admin or SNO query.
          </Alert>
        )}
        <Box sx={{ marginBottom: "24px" }}>
          <ViewForm label="Common Name" value={seedling?.commonName} />
          <ViewForm label="Botanical Name" value={seedling?.botanicalName} />
          <ViewForm
            label="No of Proposed Seedlings"
            value={proposedSeedlings}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Input
              name="numberOfPlantedSeedlings"
              label="Planted Seedlings"
              type="number"
              control={control}
              errors={errors}
              placeholder="Enter Planted Seedlings"
              rules={{
                required: errorMessage.required,
                min: {
                  value: 0,
                  message: "Planted seedlings cannot be negative",
                },
                validate: {
                  isWholeNumber: (value: any) =>
                    Number.isInteger(+value) ||
                    "Planted seedlings must be a whole number",
                  withinProposed: (value: any) =>
                    +value <= proposedSeedlings ||
                    "Exceeds available seedlings",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateInput
              name="fromDate"
              label="From date"
              control={control}
              errors={errors}
              max={new Date("10/12/3000")}
              minAge={0}
              clearErrors={clearErrors}
              rules={{
                required: errorMessage.required,
                validate: (value: any) =>
                  DATE_PATTERN.test(value || "") || "Enter a valid date",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateInput
              name="toDate"
              label="To date"
              control={control}
              errors={errors}
              max={new Date("10/12/3000")}
              minAge={0}
              clearErrors={clearErrors}
              rules={{
                required: errorMessage.required,
                validate: {
                  isValidDate: (value: any) =>
                    DATE_PATTERN.test(value || "") || "Enter a valid date",
                  afterFrom: (value: any) => {
                    const start = getValues("fromDate");
                    if (!DATE_PATTERN.test(start || "")) return true;
                    return (
                      new Date(value) >= new Date(start) ||
                      "To date cannot be before From date"
                    );
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "16px" }}>
        <Button text="Cancel" variant="outlined" onClick={onClose} />
        <Button
          text="Update"
          onClick={handleSubmit(onSubmit)}
          disabled={!isDno || !isValid || isLoading}
          loading={isLoading}
        />
      </DialogActions>
    </>
  );
};
